import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImageGeneration } from './useImageRegeneration';
import { convertImageSizeToAspectRatio, convertImageStyleToStyleType } from '../services/imageService';
import type { ImageSize } from '../components/common/ImageSizeSelect';

interface UseImageGenerationOptions {
  brandId?: string;
  content?: string;
  selectedPlatforms?: string[];
  onExistingMediaChange?: (url: string | null) => void;
  onMediaChange?: (file: File | null) => void;
  setError?: (message: string) => void;
}

export const useImageGenerationState = ({
  brandId,
  content = '',
  selectedPlatforms = [],
  onExistingMediaChange,
  onMediaChange,
  setError,
}: UseImageGenerationOptions) => {
  const { t } = useTranslation();
  const imageGeneration = useImageGeneration();
  const [isOpen, setIsOpen] = useState(false);
  const [temporaryUrl, setTemporaryUrl] = useState<string | null>(null);

  const handleGenerate = (imageStyle: string, imageSize: ImageSize) => {
    if (!brandId) {
      setError?.(t('errors.missingBrand', 'Missing brand for image generation'));
      return;
    }
    const request = {
      brandId,
      postContent: content || 'Generate an image',
      tone: 'professional',
      platforms: selectedPlatforms.length > 0 ? selectedPlatforms : ['instagram', 'linkedin'],
      aspect_ratio: convertImageSizeToAspectRatio(imageSize),
      style_type: convertImageStyleToStyleType(imageStyle),
    };
    imageGeneration.mutate(request, {
      onSuccess: (response) => {
        if (response.imageUrl) setTemporaryUrl(response.imageUrl);
      },
      onError: (error: any) => {
        setError?.(error?.message || t('errors.imageGenerationFailed', 'Image generation failed'));
      },
    });
  };

  const handleValidate = (imageUrl: string) => {
    onExistingMediaChange?.(imageUrl);
    onMediaChange?.(null);
    setTemporaryUrl(null);
    setIsOpen(false);
  };

  return {
    isOpen,
    setIsOpen,
    isLoading: imageGeneration.isPending,
    temporaryUrl,
    setTemporaryUrl,
    handleGenerate,
    handleValidate,
  };
};


